import React, { useEffect, useState } from 'react';
import { apiService } from '../../services/api';
import './BaseMapSelector.css';

interface BaseMap {
  id: string;
  name: string;
  description: string;
  thumbnailUrl: string;
  type: 'single-tile' | 'multi-tile';
  geoTiffUrl?: string;
  overviewUrl?: string;
}

interface BaseMapSelectorProps {
  selectedBaseMap: BaseMap | null;
  onSelectBaseMap: (baseMap: BaseMap) => void;
}

export const BaseMapSelector: React.FC<BaseMapSelectorProps> = ({
  selectedBaseMap,
  onSelectBaseMap,
}) => {
  const [baseMaps, setBaseMaps] = useState<BaseMap[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadBaseMaps();
  }, []);

  const loadBaseMaps = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.getBaseMapCatalog();
      
      if (response.error) {
        throw new Error(response.error);
      }

      if (response.data && response.data.baseMaps) {
        setBaseMaps(response.data.baseMaps);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load base maps';
      setError(errorMessage);
      console.error('Base map loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    loadBaseMaps();
  };

  if (loading) {
    return (
      <div className="base-map-selector">
        <h2>Select Base Map</h2>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading base maps...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="base-map-selector">
        <h2>Select Base Map</h2>
        <div className="error-state">
          <p className="error-message">{error}</p>
          <button onClick={handleRetry} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="base-map-selector">
      <h2>Select Base Map</h2>
      <div className="base-map-grid">
        {baseMaps.map((baseMap) => (
          <div
            key={baseMap.id}
            className={`base-map-card ${selectedBaseMap?.id === baseMap.id ? 'selected' : ''}`}
            onClick={() => onSelectBaseMap(baseMap)}
          >
            <div className="base-map-thumbnail">
              <img
                src={baseMap.thumbnailUrl}
                alt={baseMap.name}
                loading="lazy"
              />
            </div>
            <div className="base-map-info">
              <h3 className="base-map-name">{baseMap.name}</h3>
              <p className="base-map-description">{baseMap.description}</p>
              {baseMap.type === 'multi-tile' && (
                <span className="base-map-badge">Multi-Tile</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
